import { Fragment, memo, type FC } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import "./WizardStepper.scss";

export interface WizardStepperStep {
  key: string;
  title: string;
}

type StepStatus = "complete" | "current" | "upNext" | "pending";

interface WizardStepperProps {
  steps: WizardStepperStep[];
  currentIndex: number;
  currentCaption?: string;
  completedKeys?: string[];
  captionOverrides?: Record<string, string>;
  className?: string;
  isStepSelectable?: (_index: number) => boolean;
  onStepSelect?: (_index: number) => void;
}

const getStepStatus = (index: number, currentIndex: number): StepStatus => {
  if (index < currentIndex) {
    return "complete";
  }
  if (index === currentIndex) {
    return "current";
  }
  return index === currentIndex + 1 ? "upNext" : "pending";
};

const WizardStepper: FC<WizardStepperProps> = ({
  steps,
  currentIndex,
  currentCaption,
  completedKeys,
  captionOverrides,
  className = "",
  isStepSelectable,
  onStepSelect,
}) => {
  const { t, i18n } = useTranslation();
  const formatStepNumber = (value: number) => formatNumber(value, {}, i18n.language);

  const getCaption = (status: StepStatus, stepNumber: number) => {
    if (status === "current") {
      if (currentCaption) {
        return currentCaption;
      }
      return t("wizardStepper.stepOf", {
        step: formatStepNumber(stepNumber),
        total: formatStepNumber(steps.length),
      });
    }
    return t(`wizardStepper.${status}`);
  };

  return (
    <ol className={`wizard-stepper ${className}`}>
      {steps.map((step, index) => {
        const status =
          index !== currentIndex && completedKeys?.includes(step.key)
            ? "complete"
            : getStepStatus(index, currentIndex);
        const stepNumber = index + 1;
        const isSelectable =
          Boolean(onStepSelect) && index !== currentIndex && (isStepSelectable?.(index) ?? true);
        const content = (
          <>
            <span className="wizard-stepper__indicator" aria-hidden>
              {status === "complete" ? <Check size={14} strokeWidth={3} /> : formatStepNumber(stepNumber)}
            </span>
            <span className="wizard-stepper__text">
              <span className="wizard-stepper__title">{step.title}</span>
              <span
                className={`wizard-stepper__caption ${
                  status === "current" && currentCaption ? "wizard-stepper__caption--highlight" : ""
                }`}
              >
                {captionOverrides?.[step.key] ?? getCaption(status, stepNumber)}
              </span>
            </span>
          </>
        );

        return (
          <Fragment key={step.key}>
            {index > 0 && (
              <li
                className={`wizard-stepper__connector${
                  status === "complete" || status === "current" ? " wizard-stepper__connector--active" : ""
                }`}
                aria-hidden
              />
            )}
            <li
              className={`wizard-stepper__step wizard-stepper__step--${status}`}
              aria-current={status === "current" ? "step" : undefined}
            >
              {isSelectable ? (
                <button
                  type="button"
                  className="wizard-stepper__trigger"
                  aria-label={t("wizardStepper.goToStep", {
                    defaultValue: "Go to {{title}}",
                    title: step.title,
                  })}
                  onClick={() => onStepSelect?.(index)}
                >
                  {content}
                </button>
              ) : (
                <span className="wizard-stepper__trigger wizard-stepper__trigger--static">{content}</span>
              )}
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
};

export default memo(WizardStepper);
